import { useId, useSyncExternalStore } from 'react';
import {
  canConvertUnit,
  formatMeasure,
  formatNumber,
  fromBaseQuantity,
  getUnit,
  type ConvertibleIngredient,
  type EnteredMeasure,
  type MeasureStyle,
} from '@crave/costing';

export type { MeasureStyle } from '@crave/costing';

/** localStorage key shared by every page that shows quantities (Recipes, Inventory, Purchasing). */
export const MEASURE_STYLE_KEY = 'crave.measureStyle';

export const MEASURE_STYLE_OPTIONS: readonly (readonly [MeasureStyle, string])[] = [
  ['entered', 'As entered'],
  ['metric', 'Grams & ml'],
  ['us_weight', 'Ounces & pounds'],
  ['us_volume', 'Cups & spoons'],
  ['imperial', 'Imperial'],
];

const STYLES = new Set<string>(MEASURE_STYLE_OPTIONS.map(([v]) => v));
const isStyle = (v: unknown): v is MeasureStyle => typeof v === 'string' && STYLES.has(v);

// One store for the whole tab, so two controls on a page stay in step; the storage event keeps
// other tabs in step too.
const listeners = new Set<() => void>();
let memory: MeasureStyle = 'entered';

function read(): MeasureStyle {
  try {
    const v = window.localStorage.getItem(MEASURE_STYLE_KEY);
    if (isStyle(v)) memory = v;
  } catch {
    // Private browsing or blocked storage: keep the in-memory choice.
  }
  return memory;
}

function write(style: MeasureStyle) {
  memory = style;
  try {
    window.localStorage.setItem(MEASURE_STYLE_KEY, style);
  } catch {
    // Not persisted, still applied for this visit.
  }
  listeners.forEach((l) => l());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  const onStorage = (e: StorageEvent) => {
    if (e.key === MEASURE_STYLE_KEY) listener();
  };
  window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(listener);
    window.removeEventListener('storage', onStorage);
  };
}

/** The owner's chosen way to show quantities, remembered on this device. Default 'entered'. */
export function useMeasureStyle(): [MeasureStyle, (style: MeasureStyle) => void] {
  const style = useSyncExternalStore(subscribe, read, () => 'entered' as const);
  return [style, write];
}

export function measureStyleLabel(style: MeasureStyle): string {
  return MEASURE_STYLE_OPTIONS.find(([v]) => v === style)?.[1] ?? 'As entered';
}

const CSS = `
.crave-measure { display: flex; align-items: center; flex-wrap: wrap; gap: 6px 10px; border: 0; margin: 0; padding: 0; min-width: 0; }
.crave-measure > legend { float: left; padding: 0; margin: 0; font-weight: 600; font-size: 15px; white-space: nowrap; }
.crave-measure-options { display: flex; flex-wrap: wrap; gap: 4px; padding: 3px; min-width: 0;
  border: 1px solid var(--line); border-radius: 10px; background: var(--mist); }
.crave-measure-option { position: relative; display: inline-flex; }
.crave-measure-option input { position: absolute; inset: 0; opacity: 0; margin: 0; cursor: pointer; }
.crave-measure-option span { display: inline-flex; align-items: center; min-height: var(--touch); padding: 0 12px;
  border-radius: 7px; font-size: 14px; font-weight: 600; color: var(--ink-soft); white-space: nowrap; }
.crave-measure-option input:checked + span { background: var(--paper); color: var(--ink);
  box-shadow: 0 1px 3px rgba(20, 20, 20, 0.16); outline: 1px solid var(--line); }
.crave-measure-option input:focus-visible + span { outline: 3px solid var(--crave-red); outline-offset: 1px; }
@media (max-width: 560px) {
  .crave-measure { display: grid; }
  .crave-measure-options { display: grid; grid-template-columns: repeat(auto-fit, minmax(118px, 1fr)); }
  .crave-measure-option span { min-height: var(--touch); width: 100%; justify-content: center; }
}
`;

export interface MeasureInProps {
  /** Controlled value; omit both to use (and remember) the shared choice. */
  value?: MeasureStyle | undefined;
  onChange?: ((style: MeasureStyle) => void) | undefined;
  label?: string | undefined;
  className?: string | undefined;
}

/** A compact "Measure in" segmented control: As entered, Grams & ml, Ounces & pounds, Cups & spoons, Imperial. */
export function MeasureIn({ value, onChange, label = 'Measure in', className }: MeasureInProps) {
  const [shared, setShared] = useMeasureStyle();
  const current = value ?? shared;
  const name = useId();
  const set = (s: MeasureStyle) => {
    if (onChange) onChange(s);
    if (value === undefined) setShared(s);
  };
  return (
    <fieldset className={['crave-measure', className].filter(Boolean).join(' ')}>
      <style href="crave-measure-in" precedence="default">
        {CSS}
      </style>
      <legend>{label}</legend>
      <div className="crave-measure-options">
        {MEASURE_STYLE_OPTIONS.map(([v, l]) => (
          <label key={v} className="crave-measure-option">
            <input
              type="radio"
              name={name}
              value={v}
              checked={current === v}
              onChange={() => set(v)}
            />
            <span>{l}</span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

/** An ingredient as the admin pages have it: conversion fields plus the unit it is counted in. */
export interface MeasurableIngredient extends ConvertibleIngredient {
  display_unit?: string | null | undefined;
}

const PLURAL: Record<string, string> = { cup: 'cups', pinch: 'pinches', dash: 'dashes' };

/** Under one of these, the next smaller unit reads better: 0.26 lb is shown as 4.23 oz. */
const STEP_DOWN: Record<string, string> = {
  lb: 'oz',
  st: 'lb',
  kg: 'g',
  l: 'ml',
  gal: 'qt',
  qt: 'cup',
  imp_gal: 'imp_pt',
  imp_qt: 'imp_pt',
};

function inUnit(baseQty: number, unit: string, ingredient: MeasurableIngredient): string | null {
  let def = getUnit(unit);
  if (!def || !canConvertUnit(unit, ingredient)) return null;
  let n = fromBaseQuantity(baseQty, unit, ingredient);
  const smaller = STEP_DOWN[unit];
  const smallerDef = smaller ? getUnit(smaller) : undefined;
  if (baseQty > 0 && n < 1 && smaller && smallerDef && canConvertUnit(smaller, ingredient)) {
    def = smallerDef;
    n = fromBaseQuantity(baseQty, smaller, ingredient);
  }
  if (!Number.isFinite(n)) return null;
  const decimals = Math.abs(n) < 10 ? 2 : 1;
  const text = formatNumber(n, decimals);
  const abbr = text !== '1' && PLURAL[def.abbr] ? PLURAL[def.abbr] : def.abbr;
  return `${text} ${abbr}`;
}

/** "4 cup" as the line was entered reads as "4 cups". Packages and other units stay as they are. */
function pluralEntered(text: string, entered: EnteredMeasure): string {
  if (entered.package_id || !entered.unit || Number(entered.quantity) === 1) return text;
  const abbr = getUnit(entered.unit)?.abbr;
  if (!abbr) return text;
  const plural = PLURAL[abbr];
  if (!plural || !text.endsWith(` ${abbr}`)) return text;
  return `${text.slice(0, text.length - abbr.length)}${plural}`;
}

/**
 * The unit word for an ingredient counted one by one, from its name: "Dough ball (280 g)" reads as
 * "dough ball". Null when the name would not read naturally after a number (long, several words,
 * digits or other marks), so the caller keeps "each".
 */
export function countNoun(name: string | null | undefined): string | null {
  if (!name) return null;
  let n = name.trim();
  // Drop sizes and notes in brackets at the end: "Dough ball (280 g)", "Egg (large) (US)".
  while (/\s*\([^()]*\)$/.test(n)) n = n.replace(/\s*\([^()]*\)$/, '').trim();
  if (
    n.length === 0 ||
    n.length > 24 ||
    !/^[A-Za-z][A-Za-z'-]*(?: [A-Za-z][A-Za-z'-]*){0,2}$/.test(n)
  )
    return null;
  const words = n.split(' ');
  const first = words[0]!;
  // "Dough ball" -> "dough ball", but "San Marzano can" and "BBQ tub" keep their capitals.
  // "Crave box" keeps the brand's capital.
  const keepCaps =
    first === 'Crave' ||
    words.slice(1).some((w) => /^[A-Z]/.test(w)) ||
    !/^[A-Z][a-z'-]*$/.test(first);
  if (!keepCaps) words[0] = first.charAt(0).toLowerCase() + first.slice(1);
  // Names are usually singular; a plural name ("Eggs") is made singular first. The name is used
  // only when both its singular and its plural are certain, so no misspelled word is ever shown.
  const last = words[words.length - 1]!;
  const one = singular(last);
  if (one === null || MASS_NOUNS.has(one.toLowerCase()) || pluralNoun(one) === null) return null;
  words[words.length - 1] = one;
  return words.join(' ');
}

/**
 * Things measured rather than counted, lowercase. Named by one of these as its last word, an "each"
 * ingredient keeps "each": "2 each" of Garlic, never "2 garlics". "Garlic clove" still reads
 * "2 garlic cloves".
 */
const MASS_NOUNS: ReadonlySet<string> = new Set([
  'arugula',
  'basil',
  'bread',
  'butter',
  'cheese',
  'cornmeal',
  'cream',
  'dough',
  'flour',
  'garlic',
  'honey',
  'ice',
  'milk',
  'mozzarella',
  'oil',
  'oregano',
  'parmesan',
  'parsley',
  'pecorino',
  'pepper',
  'pepperoni',
  'prosciutto',
  'rice',
  'ricotta',
  'salt',
  'sauce',
  'semolina',
  'spinach',
  'sugar',
  'vinegar',
  'water',
  'yeast',
]);

/** Singulars whose plural is not a plain -s or -es, lowercase. */
const IRREGULAR_PLURALS: Readonly<Record<string, string>> = {
  tomato: 'tomatoes',
  potato: 'potatoes',
  leaf: 'leaves',
  loaf: 'loaves',
  half: 'halves',
  knife: 'knives',
};
const IRREGULAR_SINGULARS: Readonly<Record<string, string>> = Object.fromEntries(
  Object.entries(IRREGULAR_PLURALS).map(([one, many]) => [many, one]),
);

/** Keeps a leading capital from the name ("San Marzano Tomatoes" -> "San Marzano Tomato"). */
function sameCase(from: string, word: string): string {
  return /^[A-Z]/.test(from) ? word.charAt(0).toUpperCase() + word.slice(1) : word;
}

/** The singular of a name's last word, or null when it cannot be told for sure. */
function singular(word: string): string | null {
  const lower = word.toLowerCase();
  const known = IRREGULAR_SINGULARS[lower];
  if (known) return sameCase(word, known);
  if (!lower.endsWith('s')) return word;
  // "Eggs" -> "egg". Endings like -ies, -oes, -ves and -ses have more than one singular form
  // ("cookies", "berries"; "shoes", "potatoes"), and short words ("gas") may not be plurals at all.
  if (lower.length <= 3 || /(ies|oes|ves|ses|xes|zes|ches|shes|ss|us|is|'s)$/.test(lower))
    return null;
  return word.slice(0, -1);
}

/** The plural of a singular word, or null when it cannot be told for sure. */
function pluralNoun(word: string): string | null {
  const lower = word.toLowerCase();
  const known = IRREGULAR_PLURALS[lower];
  if (known) return sameCase(word, known);
  if (/[^aeiou]y$/.test(lower)) return `${word.slice(0, -1)}ies`;
  if (/(x|ch|sh)$/.test(lower)) return `${word}es`;
  // "tomato", "roof", "knife", "quiz", "glass": more than one way to write the plural.
  if (/(o|f|fe|z|s)$/.test(lower)) return null;
  return `${word}s`;
}

/** "2 each" of an ingredient named "Dough ball (280 g)" reads "2 dough balls"; "1 each" reads "1 dough ball". */
function countWithName(text: string, ingredient: MeasurableIngredient): string {
  if (ingredient.base_unit !== 'each') return text;
  const m = /^([−-]?)([\d.,]+) each$/.exec(text);
  if (!m) return text;
  const noun = countNoun(ingredient.name);
  if (!noun) return text;
  const n = Number(m[2]!.replace(/,/g, ''));
  if (!Number.isFinite(n)) return text;
  const words = noun.split(' ');
  if (n !== 1) {
    const many = pluralNoun(words[words.length - 1]!);
    if (many === null) return text;
    words[words.length - 1] = many;
  }
  return `${m[1]}${m[2]} ${words.join(' ')}`;
}

export interface FormatQtyOptions {
  /** A recipe or receipt line as it was typed; used by 'entered'. */
  entered?: EnteredMeasure | null | undefined;
  /** Text for a missing quantity (default "Not set"). */
  empty?: string | undefined;
}

/**
 * A base-unit quantity (g, ml or each) shown the way the owner chose. 'entered' shows the line as
 * typed when given, else the ingredient's display_unit, else grams / ml. Never throws: anything
 * that cannot convert falls back to the base unit.
 */
export function formatQty(
  baseQty: number | string | null | undefined,
  ingredient: MeasurableIngredient | null | undefined,
  style: MeasureStyle,
  options: FormatQtyOptions = {},
): string {
  const empty = options.empty ?? 'Not set';
  if (baseQty === null || baseQty === undefined || baseQty === '') return empty;
  const qty = Number(baseQty);
  if (!Number.isFinite(qty)) return empty;
  if (!ingredient) return formatNumber(qty, 2);

  // Kitchen formats are written for amounts, so format the size and put the sign in front.
  const sign = qty < 0 ? '−' : '';
  const abs = Math.abs(qty);
  // A count reads with the ingredient's name when it can: "2 dough balls", not "2 each".
  const named = (text: string) => countWithName(text, ingredient);
  const fallback = () =>
    named(
      `${sign}${formatNumber(abs, ingredient.base_unit === 'each' ? 2 : 1)} ${ingredient.base_unit}`,
    );
  try {
    if (style === 'entered') {
      if (options.entered && options.entered.quantity !== null && options.entered.quantity !== '') {
        return named(
          pluralEntered(
            formatMeasure(qty, ingredient, 'entered', options.entered),
            options.entered,
          ),
        );
      }
      if (ingredient.display_unit) {
        const shown = inUnit(abs, ingredient.display_unit, ingredient);
        if (shown) return named(`${sign}${shown}`);
      }
      return named(`${sign}${formatMeasure(abs, ingredient, 'metric')}`);
    }
    const text = formatMeasure(abs, ingredient, style);
    return text ? named(`${sign}${text}`) : fallback();
  } catch {
    return fallback();
  }
}
